/**
 * Client-side log forwarding to PostHog.
 *
 * Three sources feed the same pipe:
 *   1. console.warn / console.error  -> "client_log" events
 *   2. window "error"                -> PostHog exceptions
 *   3. window "unhandledrejection"   -> PostHog exceptions
 *
 * The original console methods are always called first, so the devtools output
 * a developer expects is never swallowed.
 *
 * Deliberately NOT captured: console.log and console.info. The storefront is
 * chatty at that level and forwarding it would drain the free-tier event
 * allowance within days. Raise LOG_LEVELS only with a quota in mind.
 */

import { EVENTS } from "./analyticsConfig";
import { getPostHog, isPostHogEnabled } from "./posthogClient";
import { getLastTraceId } from "./trace";

const LOG_LEVELS = ["warn", "error"];

// The per-page-load ceiling and the per-message repeat cap were removed on
// 2026-09-17 (see withinDiagnosticBudget). Truncation is deliberately kept: a
// 2 KB cap bounds the size of an event without ever dropping one.
const MAX_MESSAGE_LENGTH = 2000;

let installed = false;
let sentCount = 0;
const seen = new Map();
// Distinct-message ceiling for the `seen` map only — it bounds this map's own
// memory footprint, not how many events get sent (see withinDiagnosticBudget).
// A flood is almost always one message repeated, so evicting the oldest
// distinct key keeps memory flat without capping anything diagnostic.
const MAX_TRACKED_MESSAGES = 500;

/**
 * Flatten console arguments into a single string without throwing on circular
 * structures or objects with hostile toJSON/toString.
 * @param {unknown[]} args
 * @returns {string}
 */
const stringifyArgs = (args) =>
  args
    .map((arg) => {
      if (typeof arg === "string") return arg;
      if (arg instanceof Error) return `${arg.name}: ${arg.message}`;
      try {
        return JSON.stringify(arg);
      } catch {
        return String(arg);
      }
    })
    .join(" ")
    .slice(0, MAX_MESSAGE_LENGTH);

/**
 * Exported because every diagnostic sender shares one counter per page load.
 * A backend outage makes console errors and failed API calls flood together,
 * from the same cause; two independent counters would let that bill twice.
 *
 * Caps were removed by owner instruction (2026-09-17): nothing diagnostic may
 * be dropped, because Sentry is going and PostHog becomes the only record of a
 * client-side failure. A dropped event would be an error that never existed
 * anywhere. This function never gates a send; it only updates the counters
 * surfaced on every event as `log_sequence` / `log_repeat_index`, so a flood
 * is still diagnosable after the fact — but nothing stops one. THIS IS
 * UNBOUNDED BY DESIGN: one render loop can emit thousands of events per
 * minute, so the ceiling is now a PostHog billing alert, not code.
 *
 * @param {string} message
 */
export const withinDiagnosticBudget = (message) => {
  const repeats = seen.get(message) || 0;
  seen.set(message, repeats + 1);
  if (seen.size > MAX_TRACKED_MESSAGES) {
    seen.delete(seen.keys().next().value);
  }
  sentCount += 1;
};

/** Flood forensics: where this event sat in the page's diagnostic stream. */
export const diagnosticCounters = (message) => ({
  log_sequence: sentCount,
  log_repeat_index: seen.get(message) || 0,
});

/**
 * Forward one log line to PostHog. Safe to call before init and when disabled.
 * @param {"warn"|"error"|"info"} level
 * @param {string} message
 * @param {Object} [extra] additional properties to attach
 */
export const captureClientLog = (level, message, extra = {}) => {
  if (!isPostHogEnabled()) return;
  const text = typeof message === "string" ? message.slice(0, MAX_MESSAGE_LENGTH) : String(message);
  withinDiagnosticBudget(text);

  const ph = getPostHog();
  if (!ph) return;

  const traceId = getLastTraceId();

  ph.capture(EVENTS.CLIENT_LOG, {
    log_level: level,
    log_message: text,
    log_path: typeof window !== "undefined" ? window.location.pathname : undefined,
    // Correlation into Tempo. Approximate by construction — see getLastTraceId.
    trace_id: traceId,
    trace_id_is_approximate: traceId ? true : undefined,
    ...diagnosticCounters(text),
    ...extra,
  });
};

/**
 * Report an exception to PostHog's error tracking.
 * @param {unknown} error
 * @param {Object} [extra]
 */
export const captureClientError = (error, extra = {}) => {
  if (!isPostHogEnabled()) return;
  const ph = getPostHog();
  if (!ph) return;

  const key = error instanceof Error ? `${error.name}: ${error.message}` : String(error);
  // Counters are recorded but never gate an exception: an uncaught error is the
  // single most valuable thing this file sends and must not be droppable.
  withinDiagnosticBudget(key);

  const traceId = getLastTraceId();

  try {
    ph.captureException(error instanceof Error ? error : new Error(String(error)), {
      log_path: typeof window !== "undefined" ? window.location.pathname : undefined,
      // The route back to Tempo. Without this an exception reaches PostHog with
      // no correlation at all: it is not a request, so it has no trace context
      // of its own, and a session-id lookup fails whenever tail sampling has
      // already dropped the session's ordinary 200s.
      trace_id: traceId,
      trace_id_is_approximate: traceId ? true : undefined,
      ...diagnosticCounters(key),
      ...extra,
    });
  } catch {
    // Swallow: a failure to report an error must not raise another one.
  }
};

/**
 * Patch console and window error handlers. Idempotent and client-only.
 */
export const installClientLogCapture = () => {
  if (installed || typeof window === "undefined") return;
  installed = true;

  LOG_LEVELS.forEach((level) => {
    const original = console[level]?.bind(console);
    if (!original) return;

    console[level] = (...args) => {
      original(...args);
      try {
        captureClientLog(level, stringifyArgs(args));
      } catch {
        // Never let instrumentation break a console call.
      }
    };
  });

  window.addEventListener("error", (event) => {
    captureClientError(event.error || event.message, {
      log_source: "window.onerror",
      log_filename: event.filename,
      log_line: event.lineno,
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    captureClientError(event.reason, { log_source: "unhandledrejection" });
  });
};
